import { useMemo } from 'react'
import type { ReactNode } from 'react'

import type { ListFiltersDef } from './core'
import type {
    FilterFieldDef,
    FilterSearchParams,
    FilterSurface,
    FilterValues,
} from './types'
import { FilterField } from '@/components/common/FilterField'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFields = Record<string, FilterFieldDef<any>>

interface UseListFiltersArgs<T extends AnyFields> {
    def: ListFiltersDef<T>
    /** Search params hiện tại — `Route.useSearch()`. */
    search: Record<string, unknown>
    /**
     * Ghi search params mới. `replace` = true khi thay đổi đến từ ô tìm kiếm
     * (gõ liên tục) — ghi đè entry lịch sử thay vì nhét thêm, nếu không gõ 10
     * lần là phải bấm Back 10 lần mới rời được màn.
     */
    onSearchChange: (
        next: FilterSearchParams,
        opts?: { replace?: boolean },
    ) => void
    /** Số cột lưới desktop. */
    columns?: 3 | 4
    /**
     * Nhãn chip cho những field mà từ giá trị KHÔNG suy ra được nhãn — điển
     * hình là bộ lọc theo id (hộ trồng, khách hàng): id nằm trong URL nhưng tên
     * phải tra từ API. Màn tra tên rồi truyền vào đây; field nào không có nhãn
     * thì không sinh chip, vì hiện id thô lên chip người dùng không đọc được.
     */
    chipLabels?: Partial<Record<keyof T, string>>
    /**
     * Field bị ẩn ở màn này. Dùng khi một bộ lọc chỉ dành cho một số quyền —
     * vd ô chọn hộ trồng ở Nhật ký chăm sóc: tài khoản hộ dân đã bị giới hạn
     * phạm vi ở server và cũng không có `growers.read` mà ô đó cần, render ra
     * cho họ là bắn request 403.
     *
     * Ẩn nghĩa là **không có hiệu lực**: không render, không tính badge, không
     * sinh chip, và `values` trả về giá trị mặc định — để không tồn tại cảnh
     * danh sách đang bị lọc bởi một ô người dùng không nhìn thấy.
     *
     * `validateSearch` KHÔNG biết `hiddenFields` (nó chạy ngoài component, nơi
     * chưa có quyền), nên `?grower=` trong link admin gửi vẫn là param hợp lệ —
     * chỉ là không được áp dụng cho tài khoản không thấy ô đó.
     */
    hiddenFields?: readonly (keyof T)[]
}

interface BarProps<T extends AnyFields> {
    value: FilterValues<T>
    onApply: (next: FilterValues<T>) => void
    onClear: () => void
    activeCount: number
    chips: { key: string; label: string; onRemove: () => void }[]
    search?: ReactNode
    columns?: 3 | 4
    children?: (ctx: {
        value: FilterValues<T>
        patch: (partial: Partial<FilterValues<T>>) => void
        surface: 'bar' | 'sheet'
    }) => ReactNode
}

export interface UseListFiltersResult<T extends AnyFields> {
    /** Giá trị đã parse + normalize, dùng để gọi API. */
    values: FilterValues<T>
    /** Đổ thẳng vào `<ListFilterBar {...barProps} />`. */
    barProps: BarProps<T>
    /** Trang hiện tại (1 khi URL không có `page`). */
    page: number
    /** Đổi trang, GIỮ nguyên bộ lọc. Trang 1 không ghi vào URL. */
    setPage: (next: number) => void
}

/**
 * Nối config bộ lọc với URL và `ListFilterBar`.
 *
 * Ôm trọn bốn thứ trước đây rải ở từng màn: đọc/ghi URL, gộp mặc định KÈM
 * re-validate (rác trong URL không lọt tới control), tính activeCount + chip +
 * onClear, và reset `page` mỗi khi bộ lọc đổi.
 */
export function useListFilters<T extends AnyFields>({
    def,
    search,
    onSearchChange,
    columns,
    chipLabels,
    hiddenFields,
}: UseListFiltersArgs<T>): UseListFiltersResult<T> {
    // Chuỗi hóa để mảng `hiddenFields` khai inline ở call site không làm
    // `useMemo` tính lại mỗi lần render.
    const hiddenKey = (hiddenFields ?? []).join('|')
    const values = useMemo(() => {
        const parsed = def.readValues(search)
        const hidden = hiddenKey ? hiddenKey.split('|') : []
        if (hidden.length === 0) return parsed
        // Ẩn = không có hiệu lực. Ép về mặc định để màn không vô tình gọi API
        // với một bộ lọc mà người dùng không nhìn thấy và không gỡ được.
        const out = { ...parsed }
        for (const key of hidden as (keyof T)[]) {
            out[key] = def.fields[key].defaultValue
        }
        return out
    }, [def, search, hiddenKey])

    const isHidden = (key: keyof T) => !!hiddenFields?.includes(key)

    // Đổi bộ lọc luôn đưa về trang 1: giữ page cũ thì rất dễ rơi vào trang
    // trống của một tập kết quả nhỏ hơn.
    const apply = (
        next: FilterValues<T>,
        opts: { replace?: boolean } = { replace: false },
    ) => onSearchChange({ ...def.toSearch(next), page: undefined }, opts)

    const isOutside = (key: keyof T) =>
        (def.fields[key].placement ?? 'sheet') === 'outside'

    const renderFields = (
        surface: FilterSurface,
        placement: 'outside' | 'sheet',
    ) =>
        (Object.keys(def.fields) as (keyof T & string)[])
            .filter(
                (key) =>
                    !isHidden(key) &&
                    (def.fields[key].placement ?? 'sheet') === placement,
            )
            .map((key) => {
                const field = def.fields[key]
                return (
                    <FilterField key={key} label={field.label()}>
                        {(id) =>
                            field.render({
                                id,
                                value: values[key],
                                onChange: (next) =>
                                    apply(
                                        { ...values, [key]: next },
                                        { replace: isOutside(key) },
                                    ),
                                surface,
                            })
                        }
                    </FilterField>
                )
            })

    const outside = renderFields('outside', 'outside')

    // Badge và chip chỉ nói về bộ lọc ĐANG BỊ GIẤU trong sheet. Ô tìm kiếm nằm
    // ngoài, người dùng tự nhìn thấy mình đang tìm gì — đếm nó vào badge là
    // báo động giả, sinh chip cho nó là lặp lại thông tin đã hiện.
    const badgeKeys = def
        .activeKeys(values)
        .filter(
            (key) =>
                !isHidden(key) &&
                (def.fields[key].placement ?? 'sheet') !== 'outside',
        )

    const page =
        typeof search.page === 'number' && search.page > 1 ? search.page : 1

    // Màn chỉ có mỗi ô tìm kiếm thì KHÔNG có gì để bỏ vào sheet. Vẫn truyền
    // `children` là bar sẽ dựng nút "Lọc" mở ra một sheet rỗng — bấm vào thấy
    // trống trơn, người dùng tưởng hỏng.
    const sheetKeys = (Object.keys(def.fields) as (keyof T & string)[]).filter(
        (key) =>
            !isHidden(key) &&
            (def.fields[key].placement ?? 'sheet') !== 'outside',
    )

    return {
        values,
        page,
        setPage: (next) =>
            onSearchChange({
                ...def.toSearch(values),
                page: next > 1 ? next : undefined,
            }),
        barProps: {
            value: values,
            onApply: apply,
            onClear: () => apply(def.defaults),
            activeCount: badgeKeys.length,
            // Field lọc theo id không suy ra được nhãn từ giá trị; màn tra
            // tên rồi truyền qua `chipLabels`. Chưa có tên thì bỏ chip hẳn,
            // đừng hiện id thô cho người dùng đọc.
            chips: def.buildChips(values, apply, badgeKeys).flatMap((chip) => {
                const label = chipLabels?.[chip.key as keyof T] ?? chip.label
                return label ? [{ ...chip, label }] : []
            }),
            columns,
            search: outside.length > 0 ? <>{outside}</> : undefined,
            children:
                sheetKeys.length === 0
                    ? undefined
                    : (ctx) =>
                          sheetKeys.map((key) => {
                              const field = def.fields[key]
                              return (
                                  <FilterField key={key} label={field.label()}>
                                      {(id) =>
                                          field.render({
                                              id,
                                              value: ctx.value[key],
                                              onChange: (next) =>
                                                  ctx.patch({
                                                      [key]: next,
                                                  } as Partial<
                                                      FilterValues<T>
                                                  >),
                                              surface: ctx.surface,
                                          })
                                      }
                                  </FilterField>
                              )
                          }),
        },
    }
}
